import * as React from "react"
import cx from "classnames"
import { Link } from "gatsby"
import { StaticImage } from "gatsby-plugin-image"
import InferLogo from "../images/svg/logo.svg"

const platformLinks = [[{ title: "How it Works", link: "/how-it-works" }]]

const solutionsLinks = [
  [
    { title: "By Role" },
    { title: "RevOps", link: "/rev-ops" },
    { title: "Sales", link: "/sales" },
    { title: "Marketing", link: "/marketing" },
    { title: "Data", link: "/data-analytics" },
  ],
  [
    { title: "By Use Case" },
    { title: "Lead scoring", link: "/ai-lead-scoring-for-hubspot" },
    { title: "Sales metrics", link: "/metric-and-root-case-analysis" },
    { title: "Forecasting", link: "/forecasting" },
    { title: "Churn analysis", link: "/churn-analysis/" },
  ],
]

const customersLinks = [
  [
    { title: "Bruntwood Case Study", link: "/bruntwood-case-study" },
    { title: "Secret Escapes", link: "/secret-escapes" },
  ],
]

const resourcesLinks = [
  [
    //{ title: "Docs", link: "https://docs.getinfer.io/", external: true },
    { title: "Blog", link: "/blog" },
  ],
]

const companyLinks = [
  [
    { title: "About Infer", link: "/about-infer" },
    //{ title: "Contact Us", link: "/contact-us" },
  ],
]

const headerItemClass =
  "text-white text-sm-mobile mb-6 h-10 border-b border-white w-full block"

const FooterMenu = ({
  id,
  title,
  links,
  colorClass,
  titleClass,
  linkTo,
  className,
}) => {
  return (
    <div className="pt-8">
      <Link
        aria-label={`Learn about ${title}`}
        className={cx(headerItemClass, titleClass)}
        to={linkTo}
      >
        {title}
      </Link>
      <div className="flex bg-black rounded-t-none rounded-header items-start">
        {links.map((item, index) => (
          <div key={`link-${id}-${index + 1}`}>
            {item.map((cell, idx) =>
              cell.link ? (
                cell.external ? (
                  <a
                    key={`cell-${id}-${idx + 1}`}
                    aria-label={`Learn about ${cell.title}`}
                    className={cx(
                      "text-base font-light block py-2 leading-5 w-36 mr-6 text-grey-300",
                      cell.hidden ? "opacity-40 cursor-default" : colorClass
                    )}
                    href={cell.link}
                  >
                    {cell.title}
                  </a>
                ) : (
                  <Link
                    key={`cell-${id}-${idx + 1}`}
                    aria-label={`Learn about ${cell.title}`}
                    className={cx(
                      "text-base font-light block py-2 leading-5 w-36 mr-6 text-grey-300",
                      cell.hidden ? "opacity-40 cursor-default" : colorClass
                    )}
                    to={cell.link}
                  >
                    {cell.title}
                  </Link>
                )
              ) : (
                <span
                  key={`cell-${id}-${idx + 1}`}
                  className="first:mt-0 mt-6 text-base font-light block py-2 leading-5 w-36 mr-6 text-white"
                >
                  {cell.title}
                </span>
              )
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

const MobileFooterMenu = ({ id, title, links, linkTo }) => {
  const mobileLinks = links[0]
  return (
    <div>
      <Link
        aria-label={`Learn about ${title}`}
        className="pb-2 text-white w-full block text-sm-mobile pl-6 border-b border-white mb-2"
        to={linkTo}
      >
        {title}
      </Link>
      {mobileLinks.map((item, index) =>
        item.external ? (
          <a
            aria-label={`Learn about ${item.title}`}
            key={`cell-${id}-${index + 1}`}
            className={cx(
              item.hidden && "opacity-40 cursor-default",
              "text-base font-light block px-6 py-2 leading-5 text-white"
            )}
            href={item.link}
          >
            {item.title}
          </a>
        ) : (
          <Link
            aria-label={`Learn about ${item.title}`}
            key={`cell-${id}-${index + 1}`}
            className={cx(
              item.hidden && "opacity-40 cursor-default",
              "text-base font-light block px-6 py-2 leading-5 text-white"
            )}
            to={item.link}
          >
            {item.title}
          </Link>
        )
      )}
    </div>
  )
}

const Footer = ({ className }) => {
  return (
    <>
      <footer className={cx(className, "w-full py-3 bg-black desktop:hidden")}>
        <div className="px-6 pb-8 flex items-center justify-between">
          <Link
            className="text-white"
            to="/"
            aria-label={`Go to Platform page`}
          >
            <InferLogo alt="Infer logo" height={17} width={76} />
          </Link>

          <p className="text-sm text-white mt-6 mb-7">
            One Suffolk Way
            <br />
            Sevenoaks TN13 1YL
          </p>
          <div className="flex items-center space-x-3">
            <a
              href="https://www.linkedin.com/company/learntoinfer/"
              aria-label={`Visit our LinkedIn page`}
            >
              <StaticImage
                width={22}
                placeholder="blurred"
                formats={["auto", "webp", "avif"]}
                src={`../images/logos/linkedin.png`}
                alt={`linkedin logo`}
              />
            </a>
            <a
              href="https://feeds.captivate.fm/all-things-being-sql/"
              aria-label='Subscribe to our podcast "All things being SQL"'
            >
              <StaticImage
                width={23}
                height={23}
                placeholder="blurred"
                formats={["auto", "webp", "avif"]}
                src={`../images/logos/wtf.png`}
                alt={`podcast logo`}
              />
            </a>
          </div>
        </div>
        <div className="grid grid-cols-2 pb-16">
          <MobileFooterMenu
            id="platform"
            title="Platform"
            linkTo="/"
            links={platformLinks}
          />
          <MobileFooterMenu
            id="customers"
            title="Customers"
            linkTo="/bruntwood-case-study"
            links={customersLinks}
          />
        </div>
        <div className="grid grid-cols-2 pb-16">
          <MobileFooterMenu
            id="resources"
            title="Resources"
            linkTo="/blog"
            links={resourcesLinks}
          />
          <MobileFooterMenu
            id="company"
            title="Company"
            linkTo="/about-infer"
            links={companyLinks}
          />
        </div>

        <div className="text-sm flex items-center justify-between px-6 text-white">
          <a
            aria-label="Learn about our Cookie Policy"
            className="underline hover:no-underline"
            href="https://app.termly.io/document/cookie-policy/fd934a51-5f01-4fc3-b315-ac6d42fba791"
          >
            Cookie Policy
          </a>
          <span>|</span>

          <a
            aria-label="Learn about our Terms & Conditions"
            className="underline hover:no-underline"
            href="https://app.termly.io/document/terms-of-use-for-website/3ca57403-2711-45de-a41f-d988f5e68071"
          >
            Terms & Conditions
          </a>
          <span>|</span>
          <a
            aria-label="Learn about our Privacy"
            className="underline hover:no-underline"
            href="https://app.termly.io/document/privacy-policy/2293385b-748e-42e7-8426-332a44f9d09e"
          >
            Privacy
          </a>
        </div>
        <div className="text-sm px-6 text-white py-4">
          <span>© {new Date().getFullYear()}, Infer Limited</span>
        </div>
      </footer>

      <div className={cx(className, "w-full p-6 hidden desktop:block")}>
        <footer className="py-16 w-full bg-black text-white rounded-header">
          <div className="w-full container flex items-start justify-between h-full">
            <FooterMenu
              id="platform"
              title="Platform"
              linkTo="/"
              links={platformLinks}
              titleClass="hover:text-yellow-500"
              colorClass="hover:text-yellow-100"
            />
            <FooterMenu
              id="solutions"
              title="Solutions"
              linkTo="/"
              links={solutionsLinks}
              titleClass="hover:text-purple-500"
              colorClass="hover:text-purple-100"
            />
            <FooterMenu
              id="customers"
              title="Our customers"
              linkTo="/bruntwood-case-study"
              links={customersLinks}
              titleClass="hover:text-green-500"
              colorClass="hover:text-green-100"
            />
            <FooterMenu
              id="resources"
              title="Resources"
              linkTo="/blog"
              links={resourcesLinks}
              titleClass="hover:text-red-300"
              colorClass="hover:text-red-100"
            />
            <FooterMenu
              id="company"
              title="Company"
              linkTo="/about-infer"
              links={companyLinks}
              titleClass="hover:text-blue-500"
              colorClass="hover:text-blue-200"
            />
            <div className="flex-1 min-w-24">
              <Link
                aria-label="Go to Platform page"
                className="text-white max-w-48 block h-[71px]"
                to="/"
              >
                <InferLogo alt="Infer logo" height={22} width={96} />
              </Link>
              <hr className="border-b w-full border-0 h-px bg-white" />
              <p className="text-sm text-white mt-6 mb-7">
                One Suffolk Way
                <br />
                Sevenoaks
                <br />
                TN13 1YL
              </p>
              <div className="flex items-center space-x-3">
                <a
                  href="https://www.linkedin.com/company/learntoinfer/"
                  aria-label="Visit our Linkedin page"
                >
                  <StaticImage
                    width={22}
                    placeholder="blurred"
                    formats={["auto", "webp", "avif"]}
                    src={`../images/logos/linkedin.png`}
                    alt={`linkedin logo`}
                  />
                </a>
                <a
                  href="https://feeds.captivate.fm/all-things-being-sql/"
                  aria-label='Subscribe to our podcast "All things being SQL"'
                >
                  <StaticImage
                    width={23}
                    height={23}
                    placeholder="blurred"
                    formats={["auto", "webp", "avif"]}
                    src={`../images/logos/wtf.png`}
                    alt={`podcast logo`}
                  />
                </a>
              </div>
            </div>
          </div>
          <div className="font-mono text-sm-mobile mt-16 flex items-center container text-grey-300">
            <span className="mr-6">
              © {new Date().getFullYear()}, Infer Limited.
            </span>
            <div className="space-x-4">
              <a
                aria-label="Learn about our Cookie Policy"
                href="https://app.termly.io/document/cookie-policy/fd934a51-5f01-4fc3-b315-ac6d42fba791"
              >
                Cookie Policy
              </a>
              <span>|</span>
              <a
                aria-label="Learn about our Terms & Conditions"
                href="https://app.termly.io/document/terms-of-use-for-website/3ca57403-2711-45de-a41f-d988f5e68071"
              >
                Terms & Conditions
              </a>
              <span>|</span>
              <a
                aria-label="Learn about our Privacy"
                href="https://app.termly.io/document/privacy-policy/2293385b-748e-42e7-8426-332a44f9d09e"
              >
                Privacy
              </a>
            </div>
          </div>
        </footer>
      </div>
    </>
  )
}

export default Footer
